import * as immutable from "immutable";

export class HashSet<A> {
  /**
   * Create a new empty HashSet.
   */
  public static empty<A>(): HashSet<A> {
    return new HashSet(immutable.Set<A>());
  }

  /**
   * Create a new HashSet with elements copied from a source collection
   */
  public static fromCollection<A>(collection: Iterable<A>): HashSet<A> {
    return new HashSet(immutable.Set<A>(collection));
  }

  /**
   * The number of items in this HashSet
   */
  public size(): number {
    return this.s.size;
  }

  /**
   * Returns a new HashSet which also includes this value
   */
  public add(value: A): HashSet<A> {
    const updated = this.s.add(value);
    if (updated.size === this.s.size) {
      // Set has not changed
      return this;
    } else {
      return new HashSet(updated);
    }
  }

  /**
   * Returns a new Set which excludes this value
   */
  public delete(value: A): HashSet<A> {
    const updated = this.s.delete(value);
    if (updated.size === this.s.size) {
      // Set has not changed
      return this;
    } else {
      return new HashSet(updated);
    }
  }

  public reduce<R>(reducer: (reduction: R, value: A) => R, initialReduction: R, context?: any): R {
    return this.s.reduce(reducer, initialReduction, context);
  }

  public toArray(): A[] {
    return this.s.toArray();
  }

  /**
   * True if a value exists within this HashSet
   */
  public has(value: A): boolean {
    return this.s.has(value);
  }

  /**
   * Returns a HashSet including any value from the other HashSet that does
   * not already exist in this HashSet
   */
  public union(other: HashSet<A>): HashSet<A> {
    const updated = this.s.union(other.s);
    if (updated.size === this.s.size) {
      // Set has not changed
      return this;
    } else {
      return new HashSet(updated);
    }
  }

  /**
   * Returns a HashSet which has removed any values not also contained
   * within the other HashSet
   */
  public intersect(other: HashSet<A>): HashSet<A> {
    const updated = this.s.intersect(other.s);
    if (updated.size === this.s.size) {
      // Set has not changed
      return this;
    } else {
      return new HashSet(updated);
    }
  }

  /**
   * Returns a HashSet excluding any values contained within the other
   * HashSet
   */
  public subtract(other: HashSet<A>): HashSet<A> {
    const updated = this.s.subtract(other.s);
    if (updated.size === this.s.size) {
      // Set has not changed
      return this;
    } else {
      return new HashSet(updated);
    }
  }

  /**
   * Returns all of the values of the HashSet, as an Iterable.
   *
   * The order is unspecified.
   */
  public values(): IterableIterator<A> {
    return this.s.values();
  }

  /**
   * In case the `Collection` is not empty returns the last element of the
   * `Collection`.
   * In case the `Collection` is empty returns the optional default
   * value if provided, if no default value is provided returns undefined.
   */
  public last(notSetValue?: A): A {
    return this.s.last(notSetValue);
  }

  private constructor(set: immutable.Set<A>) {
    this.s = set;
  }

  private s: immutable.Set<A>;
}
